import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Laptop, Monitor, Server, Printer, Wifi, ShieldCheck,
  ArrowRight, Sparkles, CheckCircle, Plus, X, Send, Loader2, FileText, Building2,
} from 'lucide-react';
import FloatingNavWidget from '../components/public/FloatingNavWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_LANGS = ['pt', 'en', 'fr', 'ar', 'zh'];
const toCode = (language) => {
  const b = (language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(b) ? b : 'en';
};

const CATEGORY_ICONS = {
  business_laptops: Laptop,
  workstations: Server,
  monitors: Monitor,
  printers_peripherals: Printer,
  networking: Wifi,
  software_security: ShieldCheck,
};

const CATEGORY_BLURB = {
  business_laptops: 'Executive ultrabooks, enterprise EliteBooks and AI-ready Copilot+ PCs.',
  workstations: 'ISV-certified CAD, engineering and trading-desk workstations.',
  monitors: 'UltraWide 5K2K and Thunderbolt 4 hub displays for executive desks.',
  printers_peripherals: 'Office MFPs, dock stations and enterprise peripherals.',
  networking: 'Cloud-managed Wi-Fi, switching and SD-WAN for multi-site UAE deployments.',
  software_security: 'Microsoft 365, Defender, Intune and endpoint protection licensing.',
};


// ============================================================================
// Catalogue card — multi-image carousel (auto-rotate on hover + dots)
// ============================================================================
const CatalogueCard = ({ p, onPickBrand, onAddToQuote }) => {
  const { t } = useTranslation();
  const gallery = useMemo(() => {
    const arr = [p.hero_image_url, ...(p.gallery_urls || [])].filter(Boolean);
    return Array.from(new Set(arr)).slice(0, 6);
  }, [p]);

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!hovered || gallery.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % gallery.length), 1500);
    return () => clearInterval(t);
  }, [hovered, gallery.length]);

  useEffect(() => { if (!hovered) setIdx(0); }, [hovered]);

  return (
    <Link
      to={`/it-solutions/${p.slug || p.id}`}
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col"
      data-testid={`catalogue-card-${p.sku}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative group">
        {gallery.length > 0 && (
          <>
            {gallery.map((src, i) => (
              <img
                key={src + i}
                src={src}
                alt={`${p.name} — view ${i + 1}`}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
              />
            ))}
            {gallery.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                    className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40 hover:bg-white/70'}`}
                    aria-label={`Image ${i + 1}`}
                    data-testid={`catalogue-card-${p.sku}-dot-${i}`}
                  />
                ))}
              </div>
            )}
            {gallery.length > 1 && (
              <span
                className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-sm text-[10px] font-bold text-[#0F172A] tracking-wide"
                data-testid={`catalogue-card-${p.sku}-photo-count`}
              >
                {t('itSolutions.photos', { count: gallery.length })}
              </span>
            )}
          </>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPickBrand(p.brand); }}
          className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] mb-1 self-start hover:underline underline-offset-4"
          data-testid={`catalogue-brand-pill-${p.sku}`}
          title={`Filter catalogue by ${p.brand}`}
        >
          {p.brand}
        </button>
        <h3 className="text-sm font-extrabold text-[#2B2D42] leading-tight mb-2 line-clamp-2">{p.name}</h3>
        <p className="text-[12px] text-[#6C757D] leading-relaxed mb-4 line-clamp-3 flex-1">{p.short_description}</p>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToQuote(p); }}
          className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-[#0F172A] text-[#0F172A] text-xs font-bold hover:bg-[#0F172A] hover:text-white transition"
          data-testid={`catalogue-add-quote-${p.sku}`}
        >
          <Plus className="w-3.5 h-3.5" /> {t('itSolutions.addToQuote')}
        </button>
      </div>
    </Link>
  );
};


const ITSolutionsPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const code = toCode(language);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [], total: 0 });
  const [cms, setCms] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(searchParams.get('category') || '');
  const [activeBrand, setActiveBrand] = useState(searchParams.get('brand') || '');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteCart, setQuoteCart] = useState([]); // [{product_id, sku, name, quantity}]

  // Sync filters with URL params on each search-params change (back/forward nav)
  useEffect(() => {
    setActiveBrand(searchParams.get('brand') || '');
    setActiveCat(searchParams.get('category') || '');
    if (searchParams.get('brand') || searchParams.get('category')) {
      // Smoothly scroll to the catalogue section
      setTimeout(() => {
        document.getElementById('catalogue-grid')?.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    }
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/it-catalogue/meta`).then((r) => setMeta(r.data || { categories: [], brands: [] }));
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/it-page/settings`, { params: { lang: code } })
      .then((r) => setCms(r.data || null)).catch(() => setCms(null));
  }, [code]);

  // Localized category label (falls back to the catalogue meta label for custom categories)
  const catLabel = (cat) => t(`itSolutions.categories.${cat.key}`, { defaultValue: cat.label });

  // Convenience helper — returns the cms key OR the default fallback
  const c = (k, fallback) => (cms && cms[k] !== undefined && cms[k] !== null && cms[k] !== '') ? cms[k] : fallback;

  useEffect(() => {
    const params = { lang: code };
    if (activeCat) params.category = activeCat;
    if (activeBrand) params.brand = activeBrand;
    axios.get(`${API_URL}/api/public/it-catalogue/`, { params }).then((r) => setProducts(r.data || []));
  }, [activeCat, activeBrand, code]);

  const featured = useMemo(() => products.filter((p) => p.is_featured).slice(0, 3), [products]);

  const addToQuote = (p) => {
    setQuoteCart((prev) => {
      const existing = prev.find((it) => it.product_id === p.id);
      if (existing) {
        return prev.map((it) => (it.product_id === p.id ? { ...it, quantity: it.quantity + 1 } : it));
      }
      return [...prev, { product_id: p.id, sku: p.sku, name: p.name, quantity: 1 }];
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="it-solutions-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white pt-44 pb-28">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#00B4D8]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#FF6B35]/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-end">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-300/15 border border-amber-300/30 text-amber-300 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
                <Sparkles className="w-3 h-3" /> {c('hero_eyebrow', 'New · Institutional IT Procurement')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6">
                {c('hero_title_lead', 'IT Solutions for')}{' '}
                <span className="bg-gradient-to-r from-[#00B4D8] to-amber-300 bg-clip-text text-transparent">{c('hero_title_highlight', 'modern enterprises')}</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed font-light mb-8 max-w-xl">
                {c('hero_subtitle', 'Lenovo · HP · Dell — consolidated procurement for laptops, workstations, monitors and networking. Multi-brand consolidation, Free Zone delivery, executive service across UAE & Africa.')}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#00B4D8] to-amber-400 text-[#0F172A] font-extrabold tracking-tight shadow-xl hover:shadow-2xl hover:scale-[1.03] transition-all"
                  data-testid="hero-request-quote-btn"
                >
                  <FileText className="w-4 h-4" /> {c('hero_cta_primary', 'Request a quotation')}
                </button>
                <Link to="/contact" className="text-sm text-white/70 hover:text-white underline underline-offset-4">
                  {c('hero_cta_secondary', 'Or talk to a specialist')}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-3 gap-3">
                {meta.brands.filter((b) => b.count > 0).slice(0, 6).map((b) => (
                  <button
                    key={b.name}
                    onClick={() => {
                      setActiveBrand(b.name);
                      setTimeout(() => {
                        document.getElementById('catalogue-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 50);
                    }}
                    className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-center hover:bg-white/10 hover:border-amber-300/60 transition group cursor-pointer"
                    data-testid={`hero-brand-tile-${b.name}`}
                    title={`Browse all ${b.name} products`}
                  >
                    <div className="text-sm font-extrabold text-white tracking-tight group-hover:text-amber-200 transition">{b.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5 group-hover:text-white/70 transition">{t('itSolutions.items', { count: b.count })}</div>
                  </button>
                ))}
              </div>
              <div className="mt-4 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[12px] text-white/60 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-amber-300" />
                <span>{c('trust_badge', 'Authorised institutional reseller — Middle East & Africa')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 bg-[#f8f9fa]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-3">{c('categories_eyebrow', 'Browse by category')}</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">{c('categories_title', 'Everything your team needs')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {meta.categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.key] || Laptop;
              const isActive = activeCat === cat.key;
              const blurb = (cms?.category_blurbs && cms.category_blurbs[cat.key]) || CATEGORY_BLURB[cat.key];
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCat(isActive ? '' : cat.key)}
                  className={`text-left rounded-2xl p-6 border transition-all ${
                    isActive
                      ? 'border-[#FF6B35] bg-white shadow-xl -translate-y-1'
                      : 'border-gray-100 bg-white hover:shadow-lg hover:-translate-y-1'
                  }`}
                  data-testid={`it-category-${cat.key}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isActive ? 'bg-[#FF6B35]' : 'bg-[#023E8A]/5'
                  }`}>
                    <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-[#023E8A]'}`} />
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-extrabold text-[#2B2D42]">{catLabel(cat)}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#6C757D]">{t('itSolutions.items', { count: cat.count })}</span>
                  </div>
                  <p className="text-[13px] text-[#6C757D] leading-relaxed">{blurb}</p>
                </button>
              );
            })}
          </div>

          {/* Brand filter pills */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D] mr-2">{t('itSolutions.filterByBrand')}</span>
            {['', ...meta.brands.filter((b) => b.count > 0).map((b) => b.name)].map((b) => (
              <button
                key={b || 'all'}
                onClick={() => setActiveBrand(b)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-tight border transition ${
                  activeBrand === b
                    ? 'bg-[#0F172A] text-white border-[#0F172A]'
                    : 'bg-white text-[#0F172A] border-gray-200 hover:border-[#0F172A]'
                }`}
                data-testid={`it-brand-filter-${b || 'all'}`}
              >
                {b || t('itSolutions.allBrands')}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B35] mb-3">{c('featured_eyebrow', 'Featured this quarter')}</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">{c('featured_title', 'Flagship enterprise picks')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={`/it-solutions/${p.slug || p.id}`}
                  className="group rounded-2xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all bg-white"
                  data-testid={`featured-card-${p.sku}`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                    {p.hero_image_url && (
                      <img
                        src={p.hero_image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveBrand(p.brand); document.getElementById('catalogue-grid')?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B35] hover:underline underline-offset-4"
                        data-testid={`featured-brand-pill-${p.sku}`}
                        title={`Filter catalogue by ${p.brand}`}
                      >
                        {p.brand}
                      </button>
                      <span className="text-[10px] text-gray-400">·</span>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveCat(p.category); document.getElementById('catalogue-grid')?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="text-[10px] uppercase tracking-widest text-[#6C757D] hover:text-[#0F172A] hover:underline underline-offset-4"
                        title="Filter catalogue by category"
                      >
                        {catLabel(meta.categories.find((cc) => cc.key === p.category) || { key: p.category, label: '' })}
                      </button>
                    </div>
                    <h3 className="text-lg font-extrabold text-[#2B2D42] leading-tight mb-3">{p.name}</h3>
                    <p className="text-sm text-[#6C757D] leading-relaxed mb-5 line-clamp-3">{p.short_description}</p>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToQuote(p); }}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#0F172A] text-white text-sm font-bold hover:bg-[#1E293B] transition"
                      data-testid={`add-quote-${p.sku}`}
                    >
                      <Plus className="w-4 h-4" /> {t('itSolutions.addToQuote')}
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CATALOGUE GRID */}
      <section id="catalogue-grid" className="py-24 bg-[#f8f9fa]">
        <div className="container mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-3">{c('catalogue_eyebrow', 'Catalogue')}</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">
                {t('itSolutions.productsAvailable', { count: products.length })}
              </h2>
            </div>
            {(activeCat || activeBrand) && (
              <button
                onClick={() => { setActiveCat(''); setActiveBrand(''); }}
                className="text-xs text-[#6C757D] hover:text-[#0F172A] underline underline-offset-4"
              >
                {t('itSolutions.clearFilters')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <CatalogueCard
                key={p.id}
                p={p}
                onPickBrand={(b) => { setActiveBrand(b); document.getElementById('catalogue-grid')?.scrollIntoView({ behavior: 'smooth' }); }}
                onAddToQuote={addToQuote}
              />
            ))}
            {products.length === 0 && (
              <div className="col-span-full text-center py-16 text-[#6C757D]">
                {t('itSolutions.noProducts')}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* HOW WE DELIVER */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-3">{c('how_we_deliver_eyebrow', 'How we deliver')}</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">{c('how_we_deliver_title', 'From RFQ to deployed devices')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {(c('how_we_deliver_steps', null) || [
              { n: '01', title: 'Request quote', desc: 'Build your cart and submit your RFQ in under a minute.' },
              { n: '02', title: 'Pro-forma', desc: 'Receive an institutional pro-forma in AED or USD with FTA detail.' },
              { n: '03', title: 'Approve & PO', desc: 'Convert to a tax invoice and issue your purchase order.' },
              { n: '04', title: 'Delivery', desc: 'UAE Free Zone delivery + multi-country logistics to Africa.' },
              { n: '05', title: 'Setup', desc: 'Optional white-glove imaging, asset tagging and onboarding.' },
            ]).map((s) => (
              <div key={s.n} className="rounded-2xl border border-gray-100 p-5 hover:border-[#FF6B35] hover:shadow-lg transition-all">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF6B35] mb-2">{t('itSolutions.step', { n: s.n })}</div>
                <div className="text-base font-extrabold text-[#2B2D42] mb-2">{s.title}</div>
                <div className="text-[12px] text-[#6C757D] leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#023E8A] to-[#0F172A]">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="container mx-auto px-6 text-center relative">
          <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight">{c('final_cta_title', 'Ready to equip your team?')}</h3>
          <p className="text-white/70 mb-8 max-w-xl mx-auto font-light">
            {c('final_cta_subtitle', 'Tell us what you need. We respond with a fully-itemised institutional quote within one business day.')}
          </p>
          <button
            onClick={() => setQuoteOpen(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold tracking-tight shadow-xl hover:scale-105 transition-all"
            data-testid="footer-request-quote-btn"
          >
            <FileText className="w-5 h-5" /> {c('final_cta_button', 'Request a quotation')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* QUOTE PANEL (floating) */}
      {quoteCart.length > 0 && !quoteOpen && (
        <button
          onClick={() => setQuoteOpen(true)}
          className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF6B35] text-white font-extrabold shadow-2xl hover:scale-105 transition"
          data-testid="floating-quote-btn"
        >
          <FileText className="w-4 h-4" /> {t('itSolutions.quoteFloating', { count: quoteCart.length })}
        </button>
      )}

      {quoteOpen && (
        <QuoteDialog
          cart={quoteCart}
          onUpdateCart={setQuoteCart}
          onClose={() => setQuoteOpen(false)}
          onSubmitted={() => { setQuoteCart([]); setQuoteOpen(false); }}
        />
      )}

      <FloatingNavWidget backTo="/" />
    </div>
  );
};


// ============================================================================
// QUOTE DIALOG
// ============================================================================
const QuoteDialog = ({ cart, onUpdateCart, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    company_trn: '', country: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const updateQty = (id, delta) => {
    onUpdateCart(cart.map((it) => (it.product_id === id ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it)));
  };
  const removeItem = (id) => onUpdateCart(cart.filter((it) => it.product_id !== id));

  const submit = async () => {
    if (cart.length === 0) return;
    if (!form.company_name.trim() || !form.contact_email.trim() || !form.contact_name.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/it-quote-request/`, {
        ...form,
        items: cart.map((it) => ({ product_id: it.product_id, sku: it.sku, name: it.name, quantity: it.quantity })),
      });
      setSuccess(r.data);
    } catch (_) {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-xl font-extrabold text-[#2B2D42] mb-2">{t('itSolutions.quote.received')}</h3>
          <p className="text-sm text-[#6C757D] mb-2">
            {t('itSolutions.quote.reference')} <span className="font-mono font-bold text-[#023E8A]">{success.reference}</span>
          </p>
          <p className="text-sm text-[#6C757D] mb-6">
            {t('itSolutions.quote.receivedMsg')}
          </p>
          <button
            onClick={() => onSubmitted()}
            className="px-6 py-2.5 rounded-xl bg-[#0F172A] text-white font-bold hover:bg-[#1E293B] transition"
          >
            {t('itSolutions.quote.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white sm:rounded-2xl w-full max-w-3xl max-h-[95vh] flex flex-col shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">{t('itSolutions.quote.badge')}</div>
            <h3 className="text-lg font-extrabold">{t('itSolutions.quote.title')}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="quote-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-6">
          {/* Cart */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#6C757D] mb-3">{t('itSolutions.quote.selectedItems', { count: cart.length })}</h4>
            {cart.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-[#6C757D]">
                {t('itSolutions.quote.emptyCart')}
              </div>
            ) : (
              <ul className="space-y-2">
                {cart.map((it) => (
                  <li key={it.product_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#2B2D42] truncate">{it.name}</div>
                      <div className="text-[11px] font-mono text-[#6C757D]">{it.sku}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => updateQty(it.product_id, -1)} className="w-7 h-7 rounded-md bg-white border border-gray-200 text-sm font-bold hover:border-[#0F172A]">−</button>
                      <span className="w-8 text-center text-sm font-bold tabular-nums">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, 1)} className="w-7 h-7 rounded-md bg-white border border-gray-200 text-sm font-bold hover:border-[#0F172A]">+</button>
                    </div>
                    <button onClick={() => removeItem(it.product_id)} className="text-red-500 hover:text-red-700 ml-1">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t('itSolutions.quote.companyName')} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="quote-company" />
            <FormField label={t('itSolutions.quote.country')} value={form.country} onChange={(v) => setForm({ ...form, country: v })} placeholder="UAE" testid="quote-country" />
            <FormField label={t('itSolutions.quote.contactName')} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="quote-contact-name" />
            <FormField label={t('itSolutions.quote.contactEmail')} type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="quote-contact-email" />
            <FormField label={t('itSolutions.quote.phone')} value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="quote-contact-phone" />
            <FormField label={t('itSolutions.quote.trn')} value={form.company_trn} onChange={(v) => setForm({ ...form, company_trn: v })} testid="quote-trn" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{t('itSolutions.quote.notes')}</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
              placeholder={t('itSolutions.quote.notesPlaceholder')}
              data-testid="quote-notes"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex items-center justify-between sm:rounded-b-2xl">
          <span className="text-xs text-[#6C757D]">{t('itSolutions.quote.respondNote')}</span>
          <button
            onClick={submit}
            disabled={submitting || cart.length === 0 || !form.company_name.trim() || !form.contact_email.trim() || !form.contact_name.trim()}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.02] transition"
            data-testid="quote-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('itSolutions.quote.submitting')}</> : <><Send className="w-4 h-4" /> {t('itSolutions.quote.submit')}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
      data-testid={testid}
    />
  </div>
);

export default ITSolutionsPage;
